/* eslint-disable react/no-unused-state */
import React from "react"
import { Col, Row } from "antd"
import { CodeSnippet, ProgramNameDisplay, ProgramConversionContainer } from "./HelperComponents"
import { Typography } from "@mui/material";
import { Link as GatsbyLink } from "gatsby"
import { styled } from '@mui/system';

const Link = styled(props => <GatsbyLink {...props} />)({
  color: "#69c",
  textDecoration: "none",
  "&:hover": { color: "#8fc9ff" }
})

const ProgrammingConversionForTimestamp = () => {
  return (
    <div id={"programmingHumanReadableToEpochConversion"}>
      <Row style={{ marginTop: "50px" }}>
        <Col>
          <Typography
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              fontFamily: "Karla, sans-serif"
            }}
          >
            Convert from human-readable date to unix time
          </Typography>
        </Col>
      </Row>
      <Row style={{ margin: "30px 0 0 1px", width: "70%" }} gutter={8}>
        <ProgramConversionContainer>
          <ProgramNameDisplay children={"PHP"}/>
          <Col span={18}>
            <CodeSnippet>strtotime("15 November 2018")</CodeSnippet>
            <span style={{ fontSize: "15px" }}>(converts most English date texts) or:</span> <br/>
            <CodeSnippet>date_create('11/15/2018')->format('U')</CodeSnippet>
            <span style={{ fontSize: "15px" }}>(using DateTime class)</span>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"Python"}/>
          <Col span={18}>
            <CodeSnippet>import calendar, time; calendar.timegm(time.strptime('2000-01-01 12:34:00',</CodeSnippet>
            <CodeSnippet>'%Y-%m-%d %H:%M:%S'))</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"Ruby"}/>
          <Col span={18}>
            <CodeSnippet>Time.local(year, month, day, hour, minute, second, usec )</CodeSnippet>
            <span style={{ fontSize: "15px" }}>(or </span>
            <CodeSnippet>Time.gm</CodeSnippet>
            <span style={{ fontSize: "15px" }}> for GMT/UTC input). To display add </span>
            <CodeSnippet>.to_i</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"Java"}/>
          <Col span={18}>
            <CodeSnippet>long epoch = new java.text.SimpleDateFormat("MM/dd/yyyy</CodeSnippet>
            <CodeSnippet>HH:mm:ss").parse("01/01/1970 01:00:00").getTime() / 1000;</CodeSnippet>
            <span style={{ fontSize: "15px" }}>Timestamp in seconds, remove '/1000' for milliseconds.</span>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"VBScript/ASP"}/>
          <Col span={18}>
            <CodeSnippet>DateDiff("s", "01/01/1970 00:00:00", time field)</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"AutoIT"}/>
          <Col span={18}>
            <CodeSnippet>_DateDiff('s', "1970/01/01 00:00:00", "YYYY/MM/DD HH:MM:SS")</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"Delphi"}/>
          <Col span={18}>
            <CodeSnippet>Epoch := DateTimeToUnix(StrToDateTime(myString));</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"C"}/>
          <Col span={18}>
            <CodeSnippet>struct tm t; time_t t_of_day;
              t.tm_year = 1997-1900; t.tm_mon = 7; t.tm_mday = 27;
              t.tm_hour = 16; t.tm_min = 11; t.tm_sec = 42; t.tm_isdst = -1;
              t_of_day = mktime(&t);
              printf("seconds since the Epoch: %ld\n", (long) t_of_day);</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"R"}/>
          <Col span={18}>
            <CodeSnippet>	as.numeric(as.POSIXct("YYYY-MM-dd HH:mm:ss", tz = "GMT", origin="1970-01-01"))</CodeSnippet>
            <span style={{ fontSize: "15px" }}>The origin parameter is optional</span>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"Go"}/>
          <Col span={18}>
            <Link to={'https://play.golang.org/p/6h0A0WPxtq'} rel="noreferrer" target={'blank'}>Example code</Link>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"Adobe ColdFusion"}/>
          <Col span={18}>
            <CodeSnippet>int(parseDateTime(datetime).getTime()/1000);</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"MySQL"}/>
          <Col span={18}>
            <CodeSnippet>SELECT unix_timestamp(time)</CodeSnippet>
            <span style={{ fontSize: "15px" }}>Time format: YYYY-MM-DD HH:MM:SS or YYMMDD or YYYYMMDD</span>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"PostgreSQL"}/>
          <Col span={18}>
            <CodeSnippet>SELECT extract(epoch FROM date('2000-01-01 12:34'));</CodeSnippet>
            <span style={{ fontSize: "15px" }}>With timestamp:</span>
            <CodeSnippet>SELECT EXTRACT(EPOCH FROM TIMESTAMP WITH TIME ZONE '2018-02-16 20:38:40-08');</CodeSnippet>
            <span style={{ fontSize: "15px" }}>With interval:</span>
            <CodeSnippet>SELECT EXTRACT(EPOCH FROM INTERVAL '5 days 3 hours');</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"SQLite"}/>
          <Col span={18}>
            <CodeSnippet>SELECT strftime('%s',timestring);</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"SQL Server"}/>
          <Col span={18}>
            <CodeSnippet>SELECT DATEDIFF(s, '1970-01-01 00:00:00', time field)</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"JavaScript"}/>
          <Col span={18}>
            <CodeSnippet>{'var myDate = new Date("July 1, 1978 02:30:00");\n' +
            'var myEpoch = myDate.getTime()/1000.0;\n' +
            'document.write(myEpoch)'}</CodeSnippet>
          </Col>
        </ProgramConversionContainer>

        <ProgramConversionContainer>
          <ProgramNameDisplay children={"Unix/Linux Shell"}/>
          <Col span={18}>
            <CodeSnippet>date +%s -d"Jan 1, 1980 00:00:01"</CodeSnippet>
            <span style={{ fontSize: "15px" }}> Replace '-d' with '-ud' to input in GMT/UTC time.</span>
          </Col>
        </ProgramConversionContainer>
      </Row>
    </div>
  )
}

export default ProgrammingConversionForTimestamp
